// An address, from a static index read by byte range.
//
// There is no geocoding server behind this: `createPlaceLookup` opens the
// published planet build over HTTP range requests and the browser fetches
// only the bytes its own query needs. Nothing about a customer's address
// travels in a query string, because there is no query string.
//
// Keyboard first, because a dispatcher types an address and expects to
// arrow down and hit Enter without touching the mouse. Down/Up move,
// Enter takes the highlighted result, Escape closes the list.

import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  createPlaceLookup,
  type PlaceLookup,
  type PlaceSuggestion
} from "@wayfind/core";
import { createGeocodeQueue } from "../ops/geocode_queue";
import { config } from "../config";

let lookup: PlaceLookup | null = null;

function places(): PlaceLookup {
  lookup ??= createPlaceLookup({
    engineBaseUrl: config.engineBaseUrl,
    searchBaseUrl: config.searchBaseUrl
  });
  return lookup;
}

// Shared by every field on screen, because the limit that matters is the
// browser's connection pool and that is shared too. See ops/geocode_queue.
const queue = createGeocodeQueue<PlaceSuggestion[]>();

// Where this console last put a stop, remembered across sessions.
//
// An anchor is not a nicety here, it is the other half of the fix below.
// Cold, in a browser, the prefix "15 r" costs 766 requests unanchored and
// **20** anchored, because the index can only skip a shard when it knows
// the answer is not in it. Without one, a dispatcher in Luxembourg is
// searching Australia on every keystroke.
//
// `near` from the caller is the previous stop, which means it is null for
// the *first* stop of every job — the exact case that hurts. A restaurant
// delivers around the restaurant and a depot's rounds start where they
// started yesterday, so the last place picked is a good guess at the next
// one, and a wrong guess costs ordering, never correctness: the index is
// biased toward it, not restricted to it.
//
// Deliberately its own key rather than a field on the stored state: this
// is a cache, it is not worth a storage-version bump, and a console that
// loses it is slow once, not broken.
const ANCHOR_KEY = "wayfind.fleet.placeAnchor";

function readAnchor(): { lat: number; lon: number } | null {
  try {
    const raw = window.localStorage.getItem(ANCHOR_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { lat?: unknown; lon?: unknown };
    const lat = Number(parsed.lat);
    const lon = Number(parsed.lon);
    return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
  } catch {
    return null;
  }
}

function rememberAnchor(place: { lat: number; lon: number }): void {
  try {
    window.localStorage.setItem(ANCHOR_KEY, JSON.stringify({ lat: place.lat, lon: place.lon }));
  } catch {
    // A console with no storage searches unanchored. Slower, still right.
  }
}

// Typing asks for *completions*, never for a search. Half an address is
// made of words like "15" and "r" that live in every shard on Earth, so a
// search on one has to consult all of them: measured against the planet
// index, one `find("15 r")` was 4,960 requests across 294 of 310 shards
// and took the browser's connection pool down with it. The same prefix
// through the suggest sidecar is 20. The search only runs once, on the
// completed text of whatever the dispatcher actually picked.
function suggestLatest(
  text: string,
  near: { lat: number; lon: number } | null
): Promise<PlaceSuggestion[] | null> {
  return queue.submit(() => places().suggest(text, near));
}

export function PlaceField({
  label,
  hint,
  placeholder,
  near,
  autoFocus = false,
  onPick
}: {
  label: string;
  hint?: string;
  placeholder?: string;
  /** Bias results near the round so far. Nearest-first beats alphabetical. */
  near?: { lat: number; lon: number } | null;
  autoFocus?: boolean;
  onPick(place: { label: string; lat: number; lon: number }): void;
}): JSX.Element {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PlaceSuggestion[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const text = query.trim();
    if (text.length < 3) {
      setResults([]);
      setBusy(false);
      return;
    }
    let live = true;
    setBusy(true);
    const anchor = near ?? readAnchor();
    // Typing is faster than a range read over a planet index. The timer
    // stops a lookup per keystroke; `suggestLatest` stops a second one
    // from running while the first is still reading. Both are needed.
    const timer = window.setTimeout(() => {
      suggestLatest(text, anchor)
        .then(found => {
          // null means a newer lookup replaced this one before it ran. Its
          // answer is still coming, so hold the list and stay busy.
          if (!live || found == null) return;
          setResults(found);
          setHighlight(0);
          setOpen(true);
          setBusy(false);
        })
        .catch(() => { if (live) { setResults([]); setBusy(false); } });
    }, 220);
    return () => { live = false; window.clearTimeout(timer); };
  }, [query, near?.lat, near?.lon]);

  const take = useCallback((suggestion: PlaceSuggestion) => {
    // Only now does anything resolve to a point, and only for the one line
    // the dispatcher chose.
    setBusy(true);
    void places()
      .resolve(suggestion, near ?? readAnchor())
      .then(place => {
        setBusy(false);
        if (!place || place.lat == null || place.lon == null) return;
        rememberAnchor({ lat: place.lat, lon: place.lon });
        onPick({ label: place.label, lat: place.lat, lon: place.lon });
        setQuery("");
        setResults([]);
        setOpen(false);
        inputRef.current?.focus();
      })
      .catch(() => setBusy(false));
  }, [onPick, near?.lat, near?.lon]);

  return (
    <div className="field">
      <label className="field__label" htmlFor={`${listId}-input`}>{label}</label>
      <input
        id={`${listId}-input`}
        ref={inputRef}
        className="input"
        type="text"
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        autoFocus={autoFocus}
        placeholder={placeholder ?? "Street and number, or the name of the place"}
        value={query}
        onChange={event => { setQuery(event.target.value); setOpen(true); }}
        onKeyDown={event => {
          if (!results.length) return;
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setHighlight(current => (current + 1) % results.length);
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setHighlight(current => (current - 1 + results.length) % results.length);
          } else if (event.key === "Enter") {
            event.preventDefault();
            const picked = results[highlight];
            if (picked) take(picked);
          } else if (event.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && results.length ? (
        <ul className="suggests" id={listId} role="listbox">
          {results.map((suggestion, index) => (
            <li key={`${suggestion.text}-${index}`} role="option" aria-selected={index === highlight}>
              <button
                type="button"
                className="suggests__item"
                aria-selected={index === highlight}
                onMouseEnter={() => setHighlight(index)}
                onClick={() => take(suggestion)}
              >
                {suggestion.text}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <span className="field__hint">
        {busy
          ? "Looking…"
          : hint ?? "Read straight out of the map index on this device. Nothing is sent to a server."}
      </span>
    </div>
  );
}
